import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from '@silospot/i18n/react';
import { type SignupInput, signupSchema } from '@silospot/shared';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  toast,
} from '@silospot/ui';
import { useMutation } from '@tanstack/react-query';
import type { ReactElement } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom';
import { ApiError, api } from '../lib/api';
import { useAuthStore } from '../stores/auth-store';
import { useLocaleStore } from '../stores/locale-store';

export function SignupPage(): ReactElement {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const setUser = useAuthStore((state) => state.setUser);
  const locale = useLocaleStore((state) => state.locale);

  const form = useForm<SignupInput>({
    resolver: zodResolver(signupSchema),
    defaultValues: { name: '', email: '', password: '' },
  });

  const mutation = useMutation({
    mutationFn: api.signup,
    onSuccess: ({ user }) => {
      setUser(user);
      navigate('/verify-email');
    },
    onError: (error) =>
      toast.error(
        error instanceof ApiError && error.status === 409
          ? t('errors.emailTaken')
          : t('errors.generic'),
      ),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('signup.title')}</CardTitle>
        <CardDescription>
          {t('signup.hasAccount')}{' '}
          <Link to="/login" className="text-primary hover:underline">
            {t('signup.loginLink')}
          </Link>
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit((values) => mutation.mutate({ ...values, locale }))}
            className="space-y-4"
          >
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('signup.nameLabel')}</FormLabel>
                  <FormControl>
                    <Input autoComplete="name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('signup.emailLabel')}</FormLabel>
                  <FormControl>
                    <Input type="email" autoComplete="email" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('signup.passwordLabel')}</FormLabel>
                  <FormControl>
                    <Input type="password" autoComplete="new-password" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button type="submit" className="w-full" disabled={mutation.isPending}>
              {t('signup.submit')}
            </Button>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
}
